import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import colors from '@app/constants/colors';
import ErrorMessage from './ErrorMessage';

const TextInput = ({
  input,
  fieldColor,
  textColor,
  backgroundColor,
  required,
  labelId,
  type,
  attrs,
  meta: { touched, error },
  options,
}) => {
  const isError = touched && error;

  const inputParams = {
    ...input,
    color: fieldColor,
    backgroundColor,
    textColor,
    ...attrs,
    isError,
  };

  return (
    <TextFieldContainer>
      <Label htmlFor={input.name} color={fieldColor} isError={isError}>
        { !!required && '* '}
        <FormattedMessage id={labelId} />
      </Label>
      {
        type === 'select' ? (
          <Selection inputParams={inputParams} options={options} />
        ) : (
          <StyledTextInput {...inputParams} type={type} />
        )
      }
      {
        isError && <ErrorMessage error={error} />
      }
    </TextFieldContainer>
  );
};

TextInput.propTypes = {
  /* eslint-disable-next-line */
  input: PropTypes.object.isRequired, //data that passed by Field component itself
  meta: PropTypes.shape({ // data that passed by Field component itself
    touched: PropTypes.bool,
    error: PropTypes.oneOfType([ // error passed to <Field> as validate prop
      PropTypes.string,
      PropTypes.array,
    ]),
  }),
  fieldColor: PropTypes.string,
  textColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  required: PropTypes.bool,
  labelId: PropTypes.string.isRequired, // label id from translations
  type: PropTypes.string.isRequired, // input type
  attrs: PropTypes.objectOf(PropTypes.string),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    data: PropTypes.string,
  })),
};

TextInput.defaultProps = {
  meta: {},
  fieldColor: 'white',
  textColor: 'white',
  backgroundColor: 'none',
  required: false,
  attrs: {},
  options: [],
};

const Selection = ({ inputParams, options }) => (
  <StyledTextInput as="select" {...inputParams} isSelect>
    {
      options.map(({ value, data }) => (<option value={value} key={value}>{data}</option>))
    }
  </StyledTextInput>
);

Selection.propTypes = {
  inputParams: PropTypes.shape({}),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    data: PropTypes.string,
  })).isRequired,
};

Selection.defaultProps = {
  inputParams: {},
};

export default TextInput;

const StyledTextInput = styled.input`
    height: 50px;
    width: 100%;
    border-radius: 10px;
    padding: ${({ isSelect }) => (isSelect ? '0 20px' : '20px')};
    background: ${({ backgroundColor }) => (backgroundColor)};
    border: 1px solid;
    border-color: ${getColor};
    color: ${({ textColor, isError }) => getColor({ color: textColor, isError })};
    text-transform:  ${({ isSelect }) => (isSelect ? 'capitalize' : 'none')};
    transition: all 0.2s ease;
    
    &:focus {
      border-width: 2px;
    }
`;

const TextFieldContainer = styled.div`
  text-align: left;
  padding: 10px 0;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  color: ${getColor};
`;

function getColor({ color, isError }) {
  return isError ? colors.APPLE_BLOSSOM : color;
}
